import React, { createContext } from 'react';
import { status } from '../config/status';
import { sendBadgeCounter } from '../lib/messages';
import { apiConfig } from '../config/api';
import { changedThemeEvent } from '../lib/analytics';
import storage from '../lib/storage';

export const StateContext = createContext();

const capitalize = str => str.substr(0, 1).toUpperCase() + str.substr(1).toLowerCase();

class StateProvider extends React.Component {
	state = {
		theme: 'dark',
		fetching: false,
		current: null,
		tracked: [],
		config: null,
		badgeIds: []
	};

	packData({ title, data }) {
		const originLocation = data.carrier_detail?.origin_tracking_location;
		const originLocationInline = data.carrier_detail.origin_location;
		const origin = originLocation?.city
			? `${originLocation.city}, ${originLocation.country || originLocation.state}`
			: originLocationInline;
		const destinationLocation = data.carrier_detail.destination_tracking_location;
		const destinationLocationInline = data.carrier_detail.destination_location;
		const destination = destinationLocation?.city
			? `${destinationLocation.city}, ${destinationLocation.country || destinationLocation.state}`
			: destinationLocationInline.substr(0, destinationLocationInline.indexOf(','));

		return {
			title,
			code: data.tracking_code,
			origin: origin || 'UNKNOWN',
			destination,
			status: data.status,
			details: data.tracking_details
				.map(detail => {
					const location = detail.tracking_location;
					const subtitle =
						(location.zip ? location.zip + ' ' : '') +
						(location.city ? capitalize(location.city) + ' ' : '') +
						(location.country ? location.country + ' ' : '') +
						(location.state ? '- ' + location.state : '');
					// const subtitle = location.city
					// 	? `${location.zip} ${location.city}, ${location.country} - ${location.state}`
					// 	: undefined;
					const date = new Date(detail.datetime);

					return {
						origin,
						status: detail.status,
						title: detail.message,
						subtitle,
						date: `${date.toDateString().substr(date.toDateString().indexOf(' ') + 1)}`
					};
				})
				.reverse()
		};
	}

	setConfig(config) {
		this.setState(oldState => ({
			...oldState,
			config
		}));
	}

	fetchByCode({ code, title }) {
		return fetch(`${apiConfig.baseURL}/eptracking/${code}`)
			.then(res => res.json())
			.then(res => {
				if (res.status.toLowerCase() === 'success') {
					this.setState(oldState => ({
						...oldState,
						current: this.packData({ title, data: res.data })
					}));
					return this.addToTrackedItems({ title, data: res.data });
				}
			});
	}

	fetchByCodeAppProxy({ title, code }) {
		this.setState(oldState => ({ ...oldState, fetching: true }));
		this.fetchByCode({ title, code }).then(() => {
			this.setState(oldState => ({ ...oldState, fetching: false }));
		});
	}

	async addToTrackedItems({ title, data }) {
		const tracked = await storage.get('tracked');
		const code = data.tracking_code;
		const trackingDetails = data.tracking_details;
		const details = trackingDetails[trackingDetails.length - 1];
		const date = new Date(details.datetime);

		const newTracking = {
			code,
			title,
			subtitle: details.message,
			status: data.status,
			date: `${date.toDateString().substr(date.toDateString().indexOf(' ') + 1)}`,
			lastTimeFetched: Date.now(),
			detailsLength: trackingDetails.length,
			putBadge: tracked && tracked[code]?.detailsLength !== trackingDetails.length
		};

		const newTracked = {
			...tracked,
			[code]: newTracking
		};

		storage.set({ tracked: newTracked });
		this.setState(oldState => ({ ...oldState, tracked: newTracked }));

		return newTracking;
	}

	async removeTrackingItem(code) {
		let tracked = await storage.get('tracked');

		if (tracked[code]) {
			delete tracked[code];

			storage.set({ tracked });

			this.setState(oldState => ({ ...oldState, tracked }));
			this.removeFromBadgeIds(code);
		}
	}

	async fetchTracked() {
		const tracked = await storage.get('tracked');
		const limitTime = 1000 * 60 * 60;
		const promises = [];

		let badgeIds = [];

		if (!tracked) return;

		Object.values(tracked).forEach(item => {
			if (
				item.lastTimeFetched + limitTime <= Date.now() &&
				item.status !== status.delivered &&
				item.status !== status.return
			) {
				promises.push(this.fetchByCode(item));
			} else if (item.putBadge) {
				badgeIds.push(item.code);
			}
		});

		Promise.all(promises).then(responses => {
			responses.forEach(responseItem => {
				if (responseItem.putBadge) {
					badgeIds.push(responseItem.code);
				}
			});

			if (badgeIds.length > 0) {
				this.setState(oldState => ({
					...oldState,
					badgeIds
				}));

				sendBadgeCounter(badgeIds.length);
			}
		});
	}

	toggleTheme() {
		const theme = this.state.theme === 'bright' ? 'dark' : 'bright';

		this.setState(oldState => ({
			...oldState,
			theme
		}));
		storage.set({ theme });
		changedThemeEvent({ eventLabel: theme });
	}

	componentDidMount() {
		storage.get('tracked').then(tracked => {
			this.setState(oldState => ({ ...oldState, tracked }));
		});
		storage.get('theme').then(theme => {
			this.setState(oldState => ({ ...oldState, theme: theme || 'bright' }));
		});

		this.fetchTracked();
	}

	removeFromBadgeIds(id) {
		const filteredIds = this.state.badgeIds?.filter(badgeId => badgeId.toLowerCase() !== id?.toLowerCase());

		this.setState(oldState => ({ ...oldState, badgeIds: filteredIds }));

		sendBadgeCounter(filteredIds.length);
	}

	editCodeTitle({ code, title }) {
		if (!this.state.tracked || !this.state.tracked[code]) return;

		const newTracked = {
			...this.state.tracked,
			[code]: {
				...this.state.tracked[code],
				title
			}
		};

		storage.set({ tracked: newTracked });
		this.setState(oldState => ({ ...oldState, tracked: newTracked }));
	}

	render() {
		return (
			<StateContext.Provider
				value={{
					...this.state,
					toggleTheme: this.toggleTheme.bind(this),
					fetchByCode: this.fetchByCodeAppProxy.bind(this),
					removeTrackingItem: this.removeTrackingItem.bind(this),
					setConfig: this.setConfig.bind(this),
					removeFromBadgeIds: this.removeFromBadgeIds.bind(this),
					editCodeTitle: this.editCodeTitle.bind(this)
				}}
			>
				{this.props.children}
			</StateContext.Provider>
		);
	}
}

export default StateProvider;
